import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useState, useMemo, useEffect } from 'react';
import { Form } from '../../design';
import { useRequester } from '../../hooks';
import ParticipantResource from '../../resources/ParticipantResource';
import CharacterResource from '../../resources/CharacterResource';
import CharacterFormModal from './CharacterFormModal';
import useConfirmation from '../../hooks/useConfirmation';

const CREATE_NEW_CHARACTER_OPTION = '__CREATE_NEW_CHARACTER__';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: (participant: ParticipantResource) => void;
  participant?: ParticipantResource | null;
};

export default function ParticipantFormModal({
  open,
  onClose,
  onSuccess,
  participant,
}: Props) {
  const isEditMode = Boolean(participant);

  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(
    () => participant?.data.character?.id ?? null
  );
  const [characterSearchText, setCharacterSearchText] = useState('');
  const [characterModalOpen, setCharacterModalOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<CharacterResource | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedCharacterId(participant?.data.character?.id ?? null);
      fetchCharacters.submit();
    }
  }, [open, participant?.data.character?.id]);

  const fetchCharacters = useRequester<CharacterResource[]>({
    onSubmit: () => CharacterResource.fetchMany(),
    onError: console.log,
  });

  const saveParticipant = useRequester<ParticipantResource>({
    onSubmit: (data: any) =>
      isEditMode && participant
        ? ParticipantResource.updateOne(participant.data.id, data)
        : ParticipantResource.createOne(data),
    onSuccess: (savedParticipant) => {
      onSuccess(savedParticipant);
      onClose();
    },
    onError: console.log,
  });

  const deleteCharacterConfirmation = useConfirmation<CharacterResource>({
    getTitle: (char) => `Delete Character "${char.data.name || char.data.id}"?`,
    getDescription: (char) =>
      `Are you sure you want to delete this character? Participants linked to it will lose their character.`,
    onConfirm: async (char) => {
      try {
        await CharacterResource.deleteOne(char.data.id);
        CharacterResource.showAlert('Character deleted successfully', 'success');
        if (selectedCharacterId === char.data.id) {
          setSelectedCharacterId(null);
        }
        fetchCharacters.submit();
      } catch {
        CharacterResource.showAlert('Failed to delete character', 'error');
      }
    },
  });

  const handleSubmit = (data: any) => {
    const body = {
      name: data.name,
      role: data.role,
      description: data.description || '',
      avatar: data.avatar || '',
      character_id: selectedCharacterId || undefined,
    };
    saveParticipant.submit(body);
  };

  const characters = CharacterResource.list();
  const filteredCharacters = !characterSearchText.trim()
    ? characters
    : characters.filter(
      (c) =>
        (c.data.name || '')
          .toLowerCase()
          .includes(characterSearchText.toLowerCase())
    );

  const characterOptions = useMemo(() => {
    const opts = filteredCharacters.map((c) => ({
      id: c.data.id,
      label: c.data.name || c.data.id,
      avatar: c.data.avatar,
    }));
    return [
      ...opts,
      {
        id: CREATE_NEW_CHARACTER_OPTION,
        label: 'Create New Character',
        avatar: '',
      },
    ];
  }, [filteredCharacters]);

  const selectedCharacter = characters.find((c) => c.data.id === selectedCharacterId);
  const selectedCharacterOption = selectedCharacter
    ? {
      id: selectedCharacter.data.id,
      label: selectedCharacter.data.name || selectedCharacter.data.id,
      avatar: selectedCharacter.data.avatar,
    }
    : null;

  const handleCharacterChange = (optionId: string | null) => {
    if (optionId === CREATE_NEW_CHARACTER_OPTION) {
      setEditingCharacter(null);
      setCharacterModalOpen(true);
      return;
    }
    setSelectedCharacterId(optionId);
  };

  const handleCharacterSaved = (character: CharacterResource) => {
    fetchCharacters.submit();
    if (!editingCharacter) {
      setSelectedCharacterId(character.data.id);
    }
    setCharacterModalOpen(false);
    setEditingCharacter(null);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="bg-white absolute top-1/2 left-1/2 w-[800px] transform -translate-x-1/2 -translate-y-1/2 p-8 gap-4 flex flex-col">
        <Box className="flex flex-col gap-4">
          <Box className="flex justify-between">
            <Box className="flex flex-row">
              <Typography variant="h5" sx={{ fontWeight: 500 }}>
                {isEditMode ? 'Edit Participant' : 'Create New Participant'}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Form
            key={participant?.data.id || 'new'}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
            disabled={saveParticipant.loading}
          >
            <Box className="flex flex-col gap-4">
              <TextField
                required
                name="name"
                label="Name"
                fullWidth
                size="small"
                defaultValue={participant?.data.name || ''}
              />
              <TextField
                required
                name="role"
                label="Role"
                fullWidth
                size="small"
                defaultValue={participant?.data.role || ''}
              />
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={3}
                size="small"
                defaultValue={participant?.data.description || ''}
              />
              <TextField
                name="avatar"
                label="Avatar URL"
                fullWidth
                size="small"
                defaultValue={participant?.data.avatar || ''}
              />
              <Autocomplete
                value={selectedCharacterOption}
                onChange={(_, newValue) => {
                  handleCharacterChange(newValue?.id ?? null);
                }}
                onInputChange={(_, newInputValue) => {
                  setCharacterSearchText(newInputValue);
                }}
                options={characterOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Character"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedCharacterOption ? (
                        <InputAdornment position="start">
                          <Avatar
                            src={selectedCharacterOption.avatar}
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: 'primary.main',
                              fontSize: '0.875rem',
                            }}
                          >
                            {selectedCharacterOption.label.charAt(0).toUpperCase()}
                          </Avatar>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (option.id === CREATE_NEW_CHARACTER_OPTION) {
                    return (
                      <Box component="li" {...props} key={option.id}>
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: 'italic', color: 'primary.main' }}
                        >
                          + {option.label}
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box component="li" {...props} key={option.id}>
                      <Box className="flex flex-row items-center gap-2 w-full">
                        <Avatar
                          src={option.avatar}
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: 'primary.main',
                            fontSize: '0.75rem',
                          }}
                        >
                          {option.label.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box className="flex flex-col flex-grow">
                          <Typography variant="body2" fontWeight="500">
                            {option.label}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const char = characters.find((c) => c.data.id === option.id);
                            if (char) {
                              setEditingCharacter(char);
                              setCharacterModalOpen(true);
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          edge="end"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const char = characters.find((c) => c.data.id === option.id);
                            if (char) deleteCharacterConfirmation.triggerFor(char);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                }}
              />

              <Box className="flex flex-row gap-4">
                <Button
                  startIcon={
                    saveParticipant.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon sx={{ height: 20, width: 20 }} />
                    )
                  }
                  type="submit"
                  variant="contained"
                  disabled={saveParticipant.loading}
                >
                  {isEditMode ? 'Save' : 'Create'}
                </Button>
                {saveParticipant.error && (
                  <Alert severity="error">{saveParticipant.error}</Alert>
                )}
              </Box>
            </Box>
          </Form>
        </Box>

        <CharacterFormModal
          open={characterModalOpen}
          onClose={() => {
            setCharacterModalOpen(false);
            setEditingCharacter(null);
          }}
          onSuccess={handleCharacterSaved}
          character={editingCharacter}
        />
      </Box>
    </Modal>
  );
}
